import brand from '@symbo-ls/brand/context.js'
import shared from '@symbo-ls/workspace-shared'
import monaco from '@symbo-ls/workspace-monaco/context.js'
// chat (@symbo-ls/workspace-chat) is NOT eager-merged as a whole context
// anymore (chat bundle-fold, 2026-08-01) — its ~215KB component tree
// (MessengerOverlay → ChatSidebar/ChatMain/MessageList/Composer/ThreadPanel/
// Tabs + modals) folds via the shell's `ensureChatContext`
// (workspace/functions/chatFold.js) the first time any opener runs, and the
// MessengerOverlay mounts behind a `_chatCtxFolded` if-latch in app.js. The
// /chat routes fold the same context via lazySharedLibPage. The eager slice
// carries ONLY the functions non-chat surfaces reach pre-fold (shell-boot
// subscribeRealtime for toasts/chime, toast-click openChatFromToast, the
// sidebar channel-menu builders/dispatches, chatUtils) + chat's state.js —
// `messages` and `_chatDrawerOpen` are declared ONLY there and eager
// surfaces write `_chatDrawerOpen` on any route. Full inventory + caller
// evidence in @symbo-ls/workspace-chat/functions/eagerSlice.js.
import * as chatEagerFunctions from '@symbo-ls/workspace-chat/functions/eagerSlice.js'
import chatState from '@symbo-ls/workspace-chat/state.js'

const chatEager = {
  functions: { ...chatEagerFunctions },
  state: chatState
}

// mail (@symbo-ls/workspace-mail) — the built-in email client
// (architecture/MAIL.md). Same lazy shape as chat: the /mail route folds the
// FULL mail context via lazySharedLibPage (pages/index.js), and the header
// envelope folds it via ensureMailContext (functions/mailFold.js) before the
// docked MailOverlay opens — the overlay itself is mounted post-fold into the
// shell-root MailOverlaySlot (app.js), never eager. The eager slice carries
// the three tiny page-body components (self-contained render closures the
// page and the overlay bare-key), the drawer toggles (toggleMailDrawer /
// openMailDrawer / closeMailDrawer — el.call'd from any route pre-fold, like
// chat's openChatFromToast) and mail's state.js: every `mail*` key and
// `_mailDrawerOpen` are declared ONLY there, and the header flips
// `_mailDrawerOpen` on any route (v3.14 trips on undeclared root keys).
import * as mailEagerComponents from '@symbo-ls/workspace-mail/components/eagerSlice.js'
import * as mailEagerFunctions from '@symbo-ls/workspace-mail/functions/eagerSlice.js'
import mailState from '@symbo-ls/workspace-mail/state.js'

const mailEager = {
  components: { ...mailEagerComponents },
  functions: { ...mailEagerFunctions },
  state: mailState
}

// tickets (@symbo-ls/workspace-tickets) is NOT eager-merged as a whole context
// anymore (#16 tickets slim) — its ~620KB of board/kanban/triage/roadmap/
// resolutions components + function surface fold via lazySharedLibPage on first
// /tickets* or /resolutions* mount (see pages/index.js). But tickets is NOT a
// hard leaf: a SLIM eager slice must stay registered because non-tickets surfaces
// reference three of its component keys BEFORE any tickets route folds the
// context, and none of those surfaces fold tickets themselves —
//   • TicketsDetailModal is bare-keyed at the SHELL ROOT (app.js — the single
//     mounted instance since the 2026-08-06 consolidation of the 14 page
//     mounts), which parses at boot strictly before any fold can run; the
//     shell-root opener (functions/ticketDetailModal.js openTicketDetailModal)
//     seeds its `selectedTicket` carrier from any route.
//   • TicketsActivityHeatmap is bare-keyed on the HOME dashboard (main.js).
//   • TicketsSidebar / TicketsSidebarGroup are bare-keyed / string-extended by
//     the house sidebar idiom across canvas (CanvasViewSidebar,
//     ProjectCanvasSidebar), infra (InfraSidebar), the workspace GreenIG pages
//     (claims/customers/transactions/policies), shared (CanvasViewNav), and
//     /tickets/status.
// Those three component files (self-contained render closures) + the handful of
// functions they el.call from non-folding surfaces (saveTicket /
// deleteTicket / moveTicket / reloadTickets / freshTicket — see
// functions/eagerSlice.js) are imported here as the eager slice; the lazy context
// merge is add-missing, so it never double-registers them.
//
// Tickets STATE must stay seeded at boot: the HOME widgets write root.selectedTicket
// on a row click and read root.myTickets/root.tickets, and /tickets/status's sidebar
// writes the filter keys — all BEFORE any fold runs, and v3.14 root.update on an
// undeclared key trips the schema check. The full tickets state schema rides this
// slice's `state` key (exactly how it reached root while tickets sat in the eager
// array), so removing tickets from the array below loses no state declaration.
import * as ticketsEagerComponents from '@symbo-ls/workspace-tickets/components/eagerSlice.js'
import * as ticketsEagerFunctions from '@symbo-ls/workspace-tickets/functions/eagerSlice.js'
import ticketsState from '@symbo-ls/workspace-tickets/state.js'

const ticketsEager = {
  components: { ...ticketsEagerComponents },
  functions: { ...ticketsEagerFunctions },
  state: ticketsState
}

// infra (@symbo-ls/workspace-infra) is intentionally NOT eager-merged here. It
// is a hard leaf — nothing outside its own package string-extends, bare-keys, or
// el.calls any Infra* component/fn — so it is deferred via lazySharedLib and
// registered on first /infra mount. See pages/index.js ('/infra', '/infra/status').

// files (@symbo-ls/workspace-files) is NOT eager-merged as a whole context
// anymore — its ~105K of desktop components + grid/upload/viewer functions defer
// via lazySharedLibPage (see pages/index.js '/files'). Its Files* components are
// a hard leaf (nothing outside the package string-extends / bare-keys them — the
// shared FilesSidebar* family is a SEPARATE canvas-sidebar set), so they defer
// with zero risk. But files is NOT a clean function leaf: FOUR node-mutation ops
// are el.call'd by the workspace AI layer (functions/aiWorkspaceOps.js —
// aiCreateFolder/aiRenameFile/aiDeleteFile/aiMoveFileToFolder) on non-/files
// routes. Those four are carved into @symbo-ls/workspace-files/functions/nodeOps.js
// (their shared _pendingCells placement closure lives in functions/canvasGrid.js)
// and imported here as a slim eager slice; the lazy context merge is add-missing,
// so it never double-registers them. Files STATE is declared eagerly in
// state-app.js (pageContext.js reads root.fileNodes on /files AND /infra).
import {
  createCanvasFolder,
  commitRenameNode,
  deleteCanvasNode,
  moveNodesIntoFolder
} from '@symbo-ls/workspace-files/functions/nodeOps.js'
// The `files-recent` AI-answer view (provider + read + rows + open) and its
// component: the shell reads the provider on ANY route and mounts the view in
// a thread or on a Fly page without folding the files desktop (both files are
// small and self-contained; the component only extends HomeWidgetRow).
import * as filesAnswerFunctions from '@symbo-ls/workspace-files/functions/filesAnswer.js'
import { FilesAnswerView } from '@symbo-ls/workspace-files/components/FilesAnswerView.js'

const filesEager = {
  components: { FilesAnswerView },
  functions: {
    createCanvasFolder,
    commitRenameNode,
    deleteCanvasNode,
    moveNodesIntoFolder,
    ...filesAnswerFunctions
  }
}

// documents (@symbo-ls/workspace-documents) is NOT eager-merged as a whole
// context anymore — its ~65K of page-body components + fetch blocks + search
// utils defer via lazySharedLibPage (see pages/index.js '/documents' et al.).
// But documents is NOT a hard leaf: a SLIM eager slice must stay registered
// because non-documents surfaces reference it BEFORE /documents is visited —
//   • DocsSidebar (+ its section/sub-nav/folder/notes/kb closure) is string-
//     extended / bare-keyed by shared/components/{ProjectCodeSidebar,
//     TutorialsPanel,TutorialsPanelCopy}.js on canvas/editor/help routes.
//   • createNote / createKnowledgeArticle / saveKbArticle are el.call'd from
//     chat (MessageList), meet (TranscriptPage/applySuggestion), and the
//     workspace AI assistant (aiAssistant/aiWorkspaceOps) — user actions that
//     can fire without ever opening /documents.
//   • forgetDocLists is el.call'd after those writes, the AI assistant's note
//     delete and the files desktop's move back to Notes: the home Documents
//     tile caches the same note list before /documents is ever visited.
// Those three carve-outs (and only those) are imported here from their self-
// contained modules; the lazy context merge is add-missing, so it never
// double-registers them. Documents STATE is already declared eagerly in
// state-app.js, so nothing extra is seeded here.
import * as docsSidebarComponents from '@symbo-ls/workspace-documents/components/DocsSidebarExport.js'
import { createNote } from '@symbo-ls/workspace-documents/functions/createNote.js'
import { createKnowledgeArticle } from '@symbo-ls/workspace-documents/functions/createKnowledgeArticle.js'
import { saveKbArticle } from '@symbo-ls/workspace-documents/functions/saveKbArticle.js'
import { notesNavigate } from '@symbo-ls/workspace-documents/functions/notesNavigate.js'
import { forgetDocLists } from '@symbo-ls/workspace-documents/functions/docListCache.js'
// The `note-view` AI-answer view's PROVIDER: the shell reads it on ANY route
// to know the view exists; the view's component and read fold with the
// documents context first (`fold: 'documents'`, functions/assistantAppViews.js).
import { documentsAiViews } from '@symbo-ls/workspace-documents/functions/documentsAnswer.js'

const documentsEager = {
  components: { ...docsSidebarComponents },
  functions: {
    createNote,
    createKnowledgeArticle,
    saveKbArticle,
    notesNavigate,
    forgetDocLists,
    documentsAiViews
  }
}

// calendar (@symbo-ls/workspace-calendar) is NOT eager-merged as a whole context
// anymore (#16 calendar slim) — its ~200KB of week/month/team/team-now/mini/
// sidebar/main/quick-create components + its 31KB page module fold via
// lazySharedLibPage on first /calendar mount (see pages/index.js). But calendar
// is NOT a hard leaf: a SLIM eager slice must stay registered because
// non-calendar surfaces reference three of its MODAL keys BEFORE the /calendar
// route folds the context, and none of those surfaces fold calendar themselves —
//   • CalEventDetailModal is string-extended by the shell-root opener
//     workspace/functions/eventDetailModal.js (openEventDetailModal, a
//     @symbo.ls/windows custom layer) — fired on ANY route: the HOME "Coming up"
//     feed (AppSidebar / IntranetRows / LinkedResources event cells),
//     OnlineMembers, /chat, and pageContext.selectPageItem's event case.
//   • CalProfileModal is string-extended by the shell-root opener
//     workspace/functions/profileModal.js (openProfileModal) — fired on ANY
//     route: HOME OnlineMembers / IntranetRows, /chat, canvas team-and-agents +
//     tagContextMenu, and data.js.
//   • CalNewBookingModal is string-extended the same way as the other two:
//     the shell-root opener workspace/functions/newBookingModal.js
//     (openNewBookingModal, a @symbo.ls/windows custom layer) — fired on ANY
//     route: the person popover's "Meet" action, meet's openSuggestionModal,
//     and every calendar-native opener (migrated 2026-08-01 off the old
//     bare-mount + stateKey Modal pattern, mirroring the other two above).
// Those three self-contained modal files (see components/eagerSlice.js) + the
// calendar functions they el.call from non-folding surfaces (saveEvent
// / deleteEvent / updateSharedEventFields / freshBooking — also
// el.call'd by chat / meet / the workspace AI layer; see functions/eagerSlice.js)
// are imported here as the eager slice; the lazy context merge is add-missing, so
// it never double-registers them.
//
// Calendar STATE must stay seeded at boot: the shell-root openers write
// root.selectedEvent / showEventDetail / profileEmail (all declared eagerly in
// state-app.js, so already seeded), but calendar's OWN state.js keys (viewMode /
// selectedEvent / calendarEvents / calendarLoading) reached root ONLY via the
// calendar entry in this array. The full calendar state schema rides this slice's
// `state` key — exactly how it reached root while calendar sat in the eager array
// — so removing calendar from the array below loses no state declaration, and
// v3.14 root.update never trips on an undeclared calendar key.
import * as calendarEagerComponents from '@symbo-ls/workspace-calendar/components/eagerSlice.js'
import * as calendarEagerFunctions from '@symbo-ls/workspace-calendar/functions/eagerSlice.js'
import calendarState from '@symbo-ls/workspace-calendar/state.js'

const calendarEager = {
  components: { ...calendarEagerComponents },
  functions: { ...calendarEagerFunctions },
  state: calendarState
}

// meet (@symbo-ls/workspace-meet) is NOT eager-merged as a whole context
// anymore (meet+chat bundle-fold, 2026-08-01) — its ~280KB of call/room-list/
// video/transcript/suggestion UI + LiveKit/realtime function surface defer via
// lazySharedLibPage on first /meet or /meet-guest mount (see pages/index.js +
// pages/main.js's `_meetPage`). But meet is NOT a hard leaf: a SLIM eager
// slice must stay registered because non-meet surfaces reference its keys
// BEFORE any meet route folds the context, and none of those surfaces fold
// meet themselves —
//   • MeetCallWidget is bare-keyed at the shell root (workspace/app.js) — the
//     persistent floating call dock that must stay mounted (and visible)
//     across every route while a call is live. Its own mic/return/leave
//     controls `extends: 'MeetControlBarIconButton'` (split into its own file,
//     components/eagerSlice.js, so the widget doesn't drag in the whole 20KB
//     MeetControlBar component tree for one shared button base).
//   • MeetNewRoomModal is opened via a @symbo.ls/windows custom-template layer
//     by openNewRoomModal, which the GLOBAL app header's "New meeting" action
//     (shared/components/AppHeader.js + MainNavbar.js) and PersonPopover call
//     from ANY route.
//   • connectCall/disconnectCall are el.call'd from workspace pageContext.js
//     (deep-link/notification join), PersonPopover.js, chat
//     Composer.js/Main.js, and calendar EventDetailModal.js — none of which
//     fold meet.
//   • createRoom/meetRoomUrl/markSuggestionApplied are el.call'd from
//     PersonPopover.js, chat, calendar NewBookingModal.js/EventDetailModal.js,
//     and tickets TicketDetailModal.js the same way.
// meet's own state.js keys (activeRoom/meetLoading/meetTranscript) are NOT
// needed by this eager surface — none of the eager components/functions above
// read or write them (verified by grep). The state keys they DO touch
// (rooms/activeRoomId/livekitConnected/livekitParticipants/meetView/
// meetCallInFlight/meetCallError/livekitSpeakers/meetChatMessages/
// newRoomDraft/analysis/utterances) are already declared directly in
// workspace/state-app.js — meet's own state.js is fully redundant for
// boot-safety purposes, so this slice carries no `state` key (unlike
// calendar's slice, whose own state.js keys had no shell-level duplicate).
// The lazySharedLibPage merge is add-missing, so it never double-registers
// these when the full context folds in on first /meet mount.
import * as meetEagerComponents from '@symbo-ls/workspace-meet/components/eagerSlice.js'
import * as meetEagerFunctions from '@symbo-ls/workspace-meet/functions/eagerSlice.js'

const meetEager = {
  components: { ...meetEagerComponents },
  functions: { ...meetEagerFunctions }
}

// analytics (@symbo-ls/workspace-analytics) is NOT eager-merged as a whole
// context — its ~74K of dashboard/logs components + rollup/logs/map functions
// + route table defer via lazySharedLib (see pages/index.js '/analytics',
// '/workspace-analytics', … — its context.js DOES carry a `.pages` map, so
// lazySharedLib, not lazySharedLibPage). ONE thing must still be eager: the
// analytics STATE SCHEMA. Home reads nothing of it any more, but the canvas
// dashboard's Analytics button writes root._analyticsParams /
// root._analyticsProjectKey (canvas/pages/dashboard.js) before any analytics
// route is visited, and root.update on an undeclared key trips the v3.14
// schema check. analytics state is declared ONLY in analytics/state.js (not
// state-app.js), so it rides this slice's `state` key.
//
// The slice used to carry AnalyticsUsersToday + loadAnalyticsData / loadUPlot
// / uPlotTheme for the Home dashboard's "active now" widget. Home stopped
// mounting the component and calling the loader (615a93464, PRESENCE-ZERO-1:
// Home has its own `_homeWorkspaceActivity` read), so the ~47 KB rode every
// cold boot for nothing (measured 2026-09-22; the guard is
// __tests__/analyticsEagerSlice.test.js). The lazySharedLib merge is
// add-missing, so it never double-registers the state.
import analyticsState from '@symbo-ls/workspace-analytics/state.js'

const analyticsEager = {
  state: analyticsState
}

// canvas (@symbo-ls/workspace-canvas) is declared in symbols.json but, unlike
// every other library above, was never actually merged here
// (WS-SHELL-CANVAS-EAGERSLICE-MERGE-1) — the shell's own components/index.js
// carried a hand-written bridge re-export of 11 canvas component names
// instead (`export { TeamPersonNode, …, teamAndAgents } from
// '@symbo-ls/workspace-canvas/pages/team-and-agents.js'`, plus
// NewProjectPickerBody + CanvasPromptTextarea). frank's stub-phantom gate
// (FA206-STUB-PHANTOM-GATE-1) flags exactly that shape: a declared library's
// value re-exported into this project's OWN bag publishes as `""` and
// shadows the real merged entry — see smbls/plugins/frank/toJSON.js
// STUB_PHANTOM_ALLOWLIST's `system/workspace-shell` row. Canvas's own
// components/functions/methods eager slices (carved for the SAME bundle-
// splitting reason ticketsEager etc. are — see workspace/context.js's header
// note on the ~800KB full editor barrel) now double as this library's merge
// source, so this is the same shape as every entry above it.
import * as canvasEagerComponents from '@symbo-ls/workspace-canvas/components/eagerSlice.js'
import * as canvasEagerFunctions from '@symbo-ls/workspace-canvas/functions/eagerSlice.js'
import * as canvasEagerMethods from '@symbo-ls/workspace-canvas/methods/eagerSlice.js'

const canvasEager = {
  components: { ...canvasEagerComponents },
  functions: { ...canvasEagerFunctions },
  methods: { ...canvasEagerMethods }
}

// ── shared's `pages` bag is NAME-keyed, and every name becomes a ROUTE ──────
//
// WS-SETTINGS-LEGACY-PLATFORM-PAGE-CLOBBERS-ACCOUNT-1 (2026-09-02, live on
// workspace.localhost + reported by Nika): the avatar menu's "Settings" row
// (shared/functions/accountMenu.js → `router('/settings')`) opened the OLD
// PLATFORM settings page — the "Platform Options" / "Canvas" / "Design system"
// / "Website" / code-editor grid of shared/pages/settings.js — instead of the
// signed-in person's workspace account page.
//
// It is a three-step accident, and no single step is wrong on its own:
//
//   1. `packages/shared/context.js` publishes `pages` as
//      `import * as pages from './pages/index.js'` — a bag keyed by EXPORT
//      NAME (`settings`, `profile`, `login`, `lazyPage`, …), not by route.
//   2. `mergeSharedLibraries` (@symbo.ls/utils sharedLibraries.js
//      `mergeOneLibrary`) fills every key the consumer does not already hold,
//      per key. This shell's own bag is keyed by ROUTE (`'/settings'`), so all
//      71 of shared's bare names read as "missing" and are copied in.
//   3. smbls `preparePages` (packages/smbls/src/prepare.js) then promotes every
//      non-slash key: `pages['/' + name] = pages[name]; delete pages[name]`.
//      That assignment does NOT check whether `'/' + name` is already taken, so
//      shared's legacy `settings` page OVERWROTE this shell's explicit
//      `'/settings': profile` entry (pages/index.js).
//
// Measured live in the running app: `__wsRoot.routes['/settings']` held
// `{ SettingsSection, CanvasSettings, DesignSystemOptions, SyncingSettings,
// WebsiteSettings, CodeEditor, SettingsFooter }`, and four of shared's
// FUNCTION exports were registered as routes too (`/lazyPage`,
// `/lazySharedLib`, `/lazySharedLibPage`, `/foldSharedLibContext`) — the same
// promotion, visible in its harmless form.
//
// The fix is the sanctioned consumer-side merge control: a wrapped entry
// `{ library, ignoreList }` (@symbo.ls/utils `sharedLibrary` / `mergeOneLibrary`
// nested `skip.has('<bag>/<key>')`). `'pages/settings'` withholds exactly ONE
// key, so the shell keeps every other shared page it genuinely routes
// (`/invite`, `/install`, `/account`, the `*-modal` pages …) and `/settings`
// stays this shell's own lazy account route.
//
// Written as an OBJECT LITERAL, never `sharedLibrary(shared, …)`: a value that
// is the RESULT OF CALLING a function reached through an `@symbo-ls/*` import
// is a stub-phantom under frank's publish serializer (the same trap that
// hollowed 172 routes in WS-SHARED-LAZYPAGE-HOLLOW-ROUTES-SWITCH-1, see
// pages/lazyPage.js). A plain literal is inert at module-eval and survives.
// `isWrappedLibrary` accepts it — it discriminates on `ignoreList` being an
// array plus a `library`/`key`/`name` field, both of which are present here.
//
// KNOWN TWIN, deliberately NOT changed here: `login`, `signin`, `signup` and
// `profile` are promoted the same way and also displace this shell's entries.
// Those four resolve to the SAME page the shell's own lazy route would load,
// so the only loss is the route's helmet title and its lazy chunk — a cosmetic
// twin, not a wrong surface. Reported separately rather than folded into a
// user-visible bug fix.
//
// Monaco is opt-in: workspace + canvas need CodePreviewWidget, but
// analytics/marketplace/design-system/preview don't — keeps their bundles
// monaco-free.
export default [
  brand,
  { library: shared, ignoreList: ['pages/settings'] },
  monaco,
  ticketsEager,
  filesEager,
  documentsEager,
  calendarEager,
  chatEager,
  mailEager,
  meetEager,
  analyticsEager,
  canvasEager
]
